import difference from 'lodash/difference';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { actionNodeDependencyOffset } from 'flow-builder/constants';
import { createAttachNode } from 'flow-builder/hooks/node/useDisconnect/useDisconnect';

import type { NodeT } from '../../../../types';
import { styles } from '../../../renderers/node/nodeStyles';
import type { CanvasExtraData } from '../../../types/extraData';
import type { NodeAttributes } from '../../../types/node';
import type { Relation, RelationMap } from '../../../types/relation';
import { getChildOffset } from '../../canvas/calculateChildPosition/calculateChildPosition';
import { getNodeComponent } from '../getNodeComponent/getNodeComponent';

export const getRelations = (
  nodeAttributes: Partial<NodeAttributes>,
  extraData: CanvasExtraData,
  actions: (NodeT | Relation)[],
): RelationMap => {
  const relations = actions.reduce<RelationMap>((relations, node) => {
    const { id, parentId } = node;
    const { children } = relations[id] || {};

    relations[id] = {
      connections: [],
      height: styles.base.height,
      ...node,
      id,
      children: children || [],
    };

    if (parentId) {
      const parent = relations[parentId] || {};
      const parentNode = {
        ...parent,
        id: parentId,
        children: [...(parent.children || []), id],
      };

      relations[parentId] = parentNode;
    }

    return relations;
  }, {});

  /*
   * Order children & update labels.
   */
  Object.values(relations).forEach((node) => {
    const dependencies = getNodeComponent(
      nodeAttributes,
      node,
    ).translatePayload(node, extraData);
    const amountOfOuts = dependencies.length;
    const dependenciesAsChildrenArray = dependencies.reduce<Relation['id'][]>(
      (acc, { connection }) => {
        if (connection) {
          acc.push(connection);
        }

        return acc;
      },
      [],
    );

    const removedDependencies = difference(
      node.children,
      dependenciesAsChildrenArray,
    );

    node.connections = dependencies;
    node.children = dependenciesAsChildrenArray;

    removedDependencies.forEach((id) => {
      const relation = relations[id];

      if (relation.nodeType === FlowNodeTypes.ACTION) {
        delete relations[id];
      } else {
        relations[id].parentId = null;
      }
    });

    dependencies.forEach(({ connection, label }, index) => {
      if (!connection) {
        return;
      }

      if (!relations[connection]) {
        const position = index + 1;

        relations[connection] = createAttachNode({
          id: connection,
          parentId: node.id,
          pageX:
            node.pageX +
            getChildOffset(amountOfOuts, position) -
            styles.base.width / 2,
          pageY: node.pageY + (styles.base.height + actionNodeDependencyOffset),
        });
      }

      relations[connection].connectionLabel = label;
    });
  });

  return relations;
};
